// =============================================================================
// takeover-service.ts — Facility takeover mechanic (R3 Bug 8)
//
// When a player lands on an OPPONENT-owned facility they may pay 1.5× the
// printed build cost to seize it:
//   - 1.2× goes to the previous owner
//   - 0.3× is burned (bank sink)
//   - Facility ownership transfers to the lander
//
// All arithmetic uses Math.round for the split components; the two
// sub-amounts are derived from the same `total` so rounding errors stay
// within ±1T. Caller is responsible for validating affordability and tile
// ownership before calling `computeTakeoverAmounts` / `applyTakeover`.
// =============================================================================

import type { ActionLog, JsonValue } from "../rng.js";
import { assertFiniteInt } from "./_validation.js";
import type { FacilityOwnershipState, OwnedFacility } from "./facility-ownership-service.js";
import { getByTile, transferFacility } from "./facility-ownership-service.js";

// ---------------------------------------------------------------------------
// Public formula
// ---------------------------------------------------------------------------

export interface TakeoverAmounts {
  /** Total tokens debited from the lander. = Math.round(printedBuildCost * 1.5) */
  totalCost: number;
  /** Tokens credited to the previous owner. = Math.round(printedBuildCost * 1.2) */
  prevOwnerCredit: number;
  /** Tokens burned (bank sink). = totalCost - prevOwnerCredit */
  burned: number;
}

/**
 * Compute the three-way token split for a takeover.
 * `printedBuildCost` is the cost originally paid when the facility was built
 * (stored in `OwnedFacility.buildCostPaid`).
 */
export function computeTakeoverAmounts(printedBuildCost: number): TakeoverAmounts {
  assertFiniteInt(printedBuildCost, "printedBuildCost");
  if (printedBuildCost < 0) {
    throw new RangeError(`printedBuildCost must be ≥ 0, got ${printedBuildCost}`);
  }
  const totalCost = Math.round(printedBuildCost * 1.5);
  const prevOwnerCredit = Math.round(printedBuildCost * 1.2);
  const burned = totalCost - prevOwnerCredit;
  return { totalCost, prevOwnerCredit, burned };
}

// ---------------------------------------------------------------------------
// Result type
// ---------------------------------------------------------------------------

export type TakeoverErrorCode =
  | "TILE_EMPTY"
  | "TILE_SELF_OWNED"
  | "LANDER_CANNOT_AFFORD"
  | "INVALID_SEAT"
  | "INVALID_TILE";

export interface TakeoverResult {
  readonly ok: boolean;
  readonly error?: TakeoverErrorCode;
  readonly newOwnershipState: FacilityOwnershipState;
  /** Net token deltas per seat. Positive = gain, negative = loss. */
  readonly tokenDeltas: ReadonlyArray<number>; // indexed by seat 0-3
  readonly amounts: TakeoverAmounts;
  readonly log: ActionLog;
}

// ---------------------------------------------------------------------------
// Core action
// ---------------------------------------------------------------------------

/**
 * Apply a takeover of the facility at `tileIndex`.
 *
 * @param ownershipState  Current FacilityOwnershipState.
 * @param log             Caller-supplied ActionLog (pass createActionLog()).
 * @param lander          The seat landing on the tile (0-3).
 * @param landerBalance   Current token balance of the lander.
 * @param seatCount       Total seat count (typically 4). Used to size tokenDeltas.
 * @param tileIndex       Index of the tile being taken over.
 */
export function takeoverFacility(
  ownershipState: FacilityOwnershipState,
  log: ActionLog,
  lander: number,
  landerBalance: number,
  seatCount: number,
  tileIndex: number,
): TakeoverResult {
  assertFiniteInt(lander, "lander");
  assertFiniteInt(tileIndex, "tileIndex");
  assertFiniteInt(landerBalance, "landerBalance");
  assertFiniteInt(seatCount, "seatCount");

  // Look up the facility
  const facility: OwnedFacility | null = getByTile(ownershipState, tileIndex);
  if (facility === null) {
    return _fail(ownershipState, log, "TILE_EMPTY", seatCount);
  }

  // Cannot seize own facility
  if (facility.ownerSeat === lander) {
    return _fail(ownershipState, log, "TILE_SELF_OWNED", seatCount);
  }

  const amounts = computeTakeoverAmounts(facility.buildCostPaid);

  // Affordability check
  if (landerBalance < amounts.totalCost) {
    return _fail(ownershipState, log, "LANDER_CANNOT_AFFORD", seatCount);
  }

  // Transfer ownership via existing facility-ownership-service primitive
  const { newState: newOwnershipState, log: logAfterTransfer } = transferFacility(
    ownershipState,
    log,
    tileIndex,
    lander,
  );

  // Append takeover-specific log entry
  const finalLog = logAfterTransfer.append({
    kind: "takeover.applied",
    data: {
      tileIndex,
      lander,
      previousOwner: facility.ownerSeat,
      totalCost: amounts.totalCost,
      prevOwnerCredit: amounts.prevOwnerCredit,
      burned: amounts.burned,
    } as JsonValue,
  });

  // Build token delta array (sized to seatCount, zero-initialised)
  const tokenDeltas = Array<number>(seatCount).fill(0);
  tokenDeltas[lander] = -amounts.totalCost;
  tokenDeltas[facility.ownerSeat] =
    (tokenDeltas[facility.ownerSeat] ?? 0) + amounts.prevOwnerCredit;

  return {
    ok: true,
    newOwnershipState,
    tokenDeltas,
    amounts,
    log: finalLog,
  };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function _fail(
  ownershipState: FacilityOwnershipState,
  log: ActionLog,
  error: TakeoverErrorCode,
  seatCount: number,
): TakeoverResult {
  return {
    ok: false,
    error,
    newOwnershipState: ownershipState,
    tokenDeltas: Array<number>(seatCount).fill(0),
    amounts: { totalCost: 0, prevOwnerCredit: 0, burned: 0 },
    log,
  };
}
